'use client'

import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import type { ReactNode } from 'react'

import { cn } from '@src/utils/tailwind'

export interface DropdownItem {
  id: string
  name: string
  elementLeft?: ReactNode
}
export interface Props {
  items: DropdownItem[]
  selectedId?: string
  onItemIdSelected: (id?: string) => void
  placeholder?: string
  listboxOptionsClassNames?: string
  containerClassNames?: string
  size?: 'small' | 'large'
}

export const Dropdown = (props: Props) => {
  const {
    items,
    onItemIdSelected,
    selectedId,
    placeholder,
    listboxOptionsClassNames,
    size = 'small',
    containerClassNames,
  } = props
  const itemForId = items.find((item) => item.id === selectedId)
  const selected = placeholder && !itemForId ? undefined : (itemForId ?? items?.[0])

  return (
    <div className="bg-white text-black">
      <Listbox
        value={selected}
        onChange={(e) => {
          onItemIdSelected(e.id)
        }}
      >
        <ListboxButton
          className={cn(
            'relative flex w-full items-center justify-between rounded-[4px] border border-bodi-gray-butters px-4 text-left text-body-base text-bodi-gray-dark',
            'data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-bodi-gray-butters/25 focus:outline-none',
            containerClassNames,
            {
              'text-bodi-gray-medium': !selected?.name && placeholder,
              'h-[45px]': size === 'small',
              'h-[56px]': size === 'large',
            }
          )}
        >
          <div className="flex items-center gap-3">
            {selected?.elementLeft}
            {selected?.name ?? placeholder}
          </div>
          <ChevronDownIcon className="pointer-events-none size-4" aria-hidden="true" />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom"
          transition
          className={cn(
            'mt-1 w-[var(--button-width)] rounded-[4px] border border-bodi-gray-butters bg-white py-1.5 text-body-base shadow-lg [--anchor-gap:var(--spacing-1)] focus:outline-none',
            'transition duration-100 ease-in data-[closed]:opacity-0',
            listboxOptionsClassNames
          )}
        >
          {items.map((item) => (
            <ListboxOption
              key={item.name}
              value={item}
              className="group flex cursor-default select-none items-center gap-2 px-3 py-1.5 text-bodi-black data-[focus]:bg-bodi-green-bright-300"
            >
              <CheckIcon className="invisible size-4 text-bodi-green-deep-500 group-data-[selected]:visible" />
              <div className="text-body-base">{item.name}</div>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  )
}
